import { addDays, format, subDays } from 'date-fns';

/**
 * Calendar date in the user's local timezone as YYYY-MM-DD (matches backend
 * {@code ContentSearcherQueryHelper#buildSalesforceCloseDateQuery} contract).
 */
function toYmdLocal(d: Date): string {
	return format(d, 'yyyy-MM-dd');
}

function startOfTodayLocal(): Date {
	const n = new Date();
	return new Date(n.getFullYear(), n.getMonth(), n.getDate());
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Maps Salesforce close-date filter UI values to the string format expected by
 * xpsearch-aggregator: a single {@code YYYY-MM-DD} (exact match) or range
 * {@code YYYY-MM-DD:YYYY-MM-DD} (inclusive range on the indexed close-date field).
 *
 * - **today**: single date only — backend uses term query; a same-day range was too loose in practice.
 * - **last***: past through **today** (inclusive).
 * - **next***: **future only** — from **tomorrow** through the end of the window (today excluded).
 *
 * {@code custom} uses the ISO date from the date picker (single-day exact match).
 */
export function resolveSalesforceCloseDateForQuery(
	selectedOption: string | undefined,
	customIsoDate: string | undefined,
): string | undefined {
	if (!selectedOption || selectedOption === 'anytime') {
		return undefined;
	}

	if (selectedOption === 'custom') {
		if (!customIsoDate || !ISO_DATE.test(customIsoDate)) {
			return undefined;
		}
		return customIsoDate;
	}

	const today = startOfTodayLocal();
	const tomorrow = addDays(today, 1);

	switch (selectedOption) {
		case 'today':
			return toYmdLocal(today);
		case 'last7days':
			return `${toYmdLocal(subDays(today, 6))}:${toYmdLocal(today)}`;
		case 'last30days':
			return `${toYmdLocal(subDays(today, 29))}:${toYmdLocal(today)}`;
		case 'last90days':
			return `${toYmdLocal(subDays(today, 89))}:${toYmdLocal(today)}`;
		case 'last365days':
			return `${toYmdLocal(subDays(today, 364))}:${toYmdLocal(today)}`;
		case 'next7days':
			return `${toYmdLocal(tomorrow)}:${toYmdLocal(addDays(today, 7))}`;
		case 'next30days':
			return `${toYmdLocal(tomorrow)}:${toYmdLocal(addDays(today, 30))}`;
		case 'next90days':
			return `${toYmdLocal(tomorrow)}:${toYmdLocal(addDays(today, 90))}`;
		case 'next365days':
			return `${toYmdLocal(tomorrow)}:${toYmdLocal(addDays(today, 365))}`;
		default:
			// Already-resolved ISO single date or range (e.g. restored from URL)
			if (ISO_DATE.test(selectedOption)) {
				return selectedOption;
			}
			if (/^\d{4}-\d{2}-\d{2}:\d{4}-\d{2}-\d{2}$/.test(selectedOption)) {
				return selectedOption;
			}
			return undefined;
	}
}
